// src/utils/types.ts
import React, { useState, useRef, useCallback } from 'react';
import { useCart } from '../context/cartContext';
import { fetchProductsModifiers } from '../utils/api';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify'; // Import toast


// --- Import Context Hooks and Auth Utils ---
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';
import { hasRole } from './auth'; // Assuming path is correct

export interface appConfig {
    id: number,
    // tenant_id: f03d0f4e-366c-4e78-b9a3-eeef78d62197,
    title: string,
    shortname: string,
    first_name: string,
    last_name: string,
    identity_no: string,
    companyreg: string,
    tin: string,
    active: true,
    address: string,
    address_2: string,
    address_3: string,
    city: string,
    state: string,
    country: string,
    zip: string,
    phone: number,
    email: string,
    remark: string,
    domain: string,
    // db_database: string,
    // db_username: string,
    valid_starts: string,
    valid_ends: string,
    sst: number,
    service_charge: number,
    gst: number,
    themes: string,
    // deleted_at: null,
    // created_at: 2024-07-02T08:26:12.000000Z,
    // updated_at: 2024-09-10T09:53:57.000000Z
}

// src/types/config.d.ts
export interface AppConfig {
    API_URL: string;
    COMPANY_NAME: string;
    DOMAIN_NAME: string;
    DOMAIN_URL: string;
    TENANT_ID: number;
    API_KEY: string;
}

export interface User {
    id: number;
    name: string;
    email: string;
    role: string; // <-- Make sure 'role' is included based on your API response
    remember_token?: string; // The token used for authentication
    // Add any other relevant fields from your API's user object
    customer?: any; // If customer data is nested under user
}

// Update AuthContextType to use the User type and include logout
export interface AuthContextType {
    user: User | null; // Use the specific User type
    isAuthenticated: boolean;
    login: (email: string, password: string) => Promise<User>; // Should return the logged-in User
    logout: () => void; // Add a logout function signature
}

export interface ProductCardProps {
    product: Product;
    imageRef?: React.RefObject<HTMLImageElement>; // Add optional imageRef here
    
}

export interface Category {
    id: number;
    cat_name: string;
    cat_prod_qty: number;
}

// /src/utils/types.ts
export interface Brand {
    id: number;
    cat_name: string;
    brand_prod_qty: number;
}

export interface Media {
    id: number;
    original_url: string;
    preview_url: string;
    // Add other properties from media if needed
};

export interface Product {
    id: number;
    media: Media[]; // Update this to be an array of Media objects
    original_url: string;
    preview_url: string;
    product_code: string;
    product_name: string;
    product_desc: string;
    product_value: number;
    brand: number;
    MTPrice: number;
    MTPriceArray: { price: number; cat_name: string }[]; // Updated structure
    product_cat: number;
    cat_name: string;
    brand_name: string;
    product_link: string;
    details: string;
    warranty: string;
    stock: number;
    restock: number;
    restock_at: string;
    // Add any other properties your product may have
};

export const displayPrice = (price: number) => {
    // Convert price to a number if it's not already
    const numericPrice = typeof price === 'number' ? price : parseFloat(price);

    // Check if numericPrice is valid
    if (!isNaN(numericPrice)) {
        return 'RM' + numericPrice.toLocaleString('en-US', { minimumFractionDigits: 2 }); // Format to 2 decimal places
    }

    return 'N/A'; // Return a fallback value if price is invalid
};

export const formatProductNameForUrl = (name: string) => {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric characters with hyphens
        .replace(/-+$/, '');          // Remove trailing hyphens
};

export interface CartItem {
    cartItemId: string; // Unique ID for each cart item
    id: number; // product id
    product_category: number;
    product_id: number;
    product_code: string;
    product_name: string;
    product_value: number;

    price: number;
    quantity: number;
    payment_for: string;
    modifiers: Modifier[]; // Add modifiers to the cart item
    // imageRef?: React.RefObject<HTMLImageElement>; // Add imageRef to CartItem
}

// New Modifier interface 250323
export interface Modifier {
    id: number;
    product_id: number;
    product_name: string;
    product_value: number;
    // Add other properties as needed

    // id: number;
    user_id: number;
    media_id: number | null;
    product_uuid: string;
    product_code: string;
    product_code1: string | null;
    product_code2: string | null;
    product_code3: string | null;
    // product_name: string;
    product_desc: string | null;
    product_type: string | null;
    product_cat: string;
    product_addon_cat: number[];  //string[];
    product_unit_m: string;
    // product_value: string;
    product_link: string | null;
    product_path: string | null;
    brand: string | null;
    warranty: string;
    details: string | null;
    stock: number | null;
    restock: number | null;
    restock_at: string;
    active: boolean;
    cond: boolean;
    is_modifier: boolean;
    vat_active: boolean;
    approved_by: string | null;
    approved_at: string;
    remark: string | null;
    deleted_at: string | null;
    created_at: string;
    updated_at: string;
    vat_price: string;
    vat_tax: string;
}

export interface CartState {
    items: CartItem[];
}

export interface CartAction {
    type: string;
    payload: CartItem | CartItem[];
}

export interface CartProviderProps {
    children: React.ReactNode;
}

// New type for Product with imageRef
export interface ProductWithImageRef extends Product {
    imageRef?: React.RefObject<HTMLImageElement>;
}

export interface ProductWithImageRef extends Product {
    imageRef?: React.RefObject<HTMLImageElement>;
}

export const useHandleAddToCart = () => {
    const { dispatch } = useCart();
    const [showModifierModal, setShowModifierModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductWithImageRef | null>(null);
    const [modifiers, setModifiers] = useState<Modifier[]>([]);
    const [selectedModifiers, setSelectedModifiers] = useState<Modifier[]>([]);
    const [animationQueue, setAnimationQueue] = useState<{ product: ProductWithImageRef, imageRef: React.RefObject<HTMLImageElement> }[]>([]);

    // --- Get Contexts for Effective User Calculation ---
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer } = useCustomerContext();

    // --- Implement getEffectiveUserDetails directly using context ---
    // (Mirrors the logic from Home.tsx/ProductCard.tsx)
    const getEffectiveUserDetails = useCallback(() => {
        const isAdminOrStaff = hasRole(['super_admin', 'staff']);
        const isAdminActingAs = isAdminOrStaff && selectedSessionCustomer;

        if (isAdminActingAs) {
            const customerRole = selectedSessionCustomer?.role || 'dealer'; // Default or fetched role

            console.log('getEffectiveUserDetails - Acting As:', selectedSessionCustomer); // Add this for debug

            return {
                id: selectedSessionCustomer.user_id,
                role: customerRole,
                isAuthenticated: true,
                customer_id: selectedSessionCustomer.id // Pass customer ID too
            };
        } else {
            let loggedInUserRole: string | undefined;
            try {
                const rolesPermissionsItem = localStorage.getItem('rolesPermissions');
                if (rolesPermissionsItem) {
                    const rolesPermissions = JSON.parse(rolesPermissionsItem);
                    loggedInUserRole = rolesPermissions?.roles?.[0];
                }
            } catch (e) { console.error("Failed to parse rolesPermissions in useHandleAddToCart:", e); }
            return {
                id: loggedInUser?.id,
                role: loggedInUserRole,
                isAuthenticated: !!loggedInUser,
                customer_id: loggedInUser?.customer?.id || loggedInUser?.id // Adjust based on structure
            };
        }
    }, [loggedInUser, selectedSessionCustomer]); // Dependencies for the calculation


    // --- Update handleAddToCart signature ---
    const handleAddToCart = useCallback(async (
        product: Product,
        imageRef?: React.RefObject<HTMLImageElement>,
        // Receive price/user details from ProductCard
        priceToAdd?: number, // Price passed from ProductCard (could be MTPrice or SRP)
        effectiveUserId?: number,
        effectiveUserRole?: string
    ) => {
        // Ensure we have the necessary details, even if passed
        const effectiveUser = getEffectiveUserDetails();

        // console.log("handleAddToCart - effectiveUser", effectiveUser); // Add this


        const userId = effectiveUserId ?? effectiveUser.id;
        const userRole = effectiveUserRole ?? effectiveUser.role;
        // Use the passed price, or fallback to product_value (SRP) if not passed
        const finalPriceToAdd = priceToAdd ?? product.product_value;

        if (!effectiveUser.isAuthenticated || !userId || !userRole) {
            toast.error("Please log in to add items to the cart.");
            console.error("Add to cart failed: User not authenticated or details missing.");
            return;
        }

        try {
            const response = await fetchProductsModifiers(product.id, 2); // Check for modifiers
            if (response.data && response.data.length > 0) {
                // Modifiers exist, open modal
                setModifiers(response.data);
                // Store the base price and user details with the selected product for the modal
                setSelectedProduct({
                    ...product,
                    imageRef,
                    // Store the price that was determined in ProductCard
                    // We'll use this as the base for modifier calculations
                    MTPrice: finalPriceToAdd, // Use MTPrice property to store the base price for modal
                });
                setShowModifierModal(true);
            } else {
                // No modifiers, add directly to cart
                addToCart(
                    { ...product, imageRef },
                    [], // No modifiers
                    imageRef,
                    finalPriceToAdd, // Use the price determined in ProductCard
                    userId,
                    userRole,
                    effectiveUser.customer_id // Pass the correct customer ID
                );
            }
        } catch (error) {
            console.error('Error fetching modifiers or adding to cart:', error);
            toast.error("Failed to add item to cart.");
            // Optionally add to cart with SRP as a fallback on error?
            // addToCart({ ...product, imageRef }, [], imageRef, product.product_value, userId, userRole, effectiveUser.customer_id);
        }
    }, [getEffectiveUserDetails]); // Add dependency


    const handleCloseModifierModal = () => {
        setShowModifierModal(false);
        setSelectedProduct(null);
        setModifiers([]);
        setSelectedModifiers([]);
    };

    const handleModifierSelection = (modifier: Modifier) => {
        setSelectedModifiers((prevSelected) => {
            const isSelected = prevSelected.some((m) => m.id === modifier.id);
            if (isSelected) {
                return prevSelected.filter((m) => m.id !== modifier.id);
            } else {
                // Ensure only one 'cond: true' modifier is selected if needed
                // This logic might need adjustment based on exact requirements
                let newSelection = [...prevSelected];
                if (modifier.cond) {
                    // Remove other 'cond: true' modifiers before adding the new one
                    newSelection = newSelection.filter(m => !m.cond);
                }
                return [...newSelection, modifier];
            }
        });
    };

    // --- Update handleConfirmModifiers ---
    const handleConfirmModifiers = useCallback(() => {
        if (selectedProduct) {
            // Get effective user details
            const effectiveUser = getEffectiveUserDetails(); // Call the implemented function
            console.log("handleConfirmModifiers - effectiveUser", effectiveUser); // Add this


            if (!effectiveUser.isAuthenticated || !effectiveUser.id || !effectiveUser.role) {
                toast.error("User details missing, cannot confirm modifiers.");
                console.error("Confirm modifiers failed: User details missing.");
                handleCloseModifierModal();
                return;
            }

            // Use the base price stored when opening the modal
            const basePrice = selectedProduct.MTPrice ?? selectedProduct.product_value; // Fallback to SRP

            // Calculate modifier price (same logic as before)
            let modifierTotalPrice = 0;
            let hasCondTrue = false;
            selectedModifiers.forEach(modifier => {
                // Ensure product_value is treated as a number
                const modifierValue = Number(modifier.product_value) || 0;
                if (modifier.cond) {
                    if (!hasCondTrue) {
                        modifierTotalPrice += modifierValue;
                        hasCondTrue = true;
                    }
                } else {
                    modifierTotalPrice += modifierValue;
                }
            });

            const finalPrice = Number(basePrice) + modifierTotalPrice;

            // Pass details to addToCart
            addToCart(
                selectedProduct,
                selectedModifiers,
                selectedProduct.imageRef,
                finalPrice, // Pass the calculated final price
                effectiveUser.id, // Pass effective user ID
                effectiveUser.role, // Pass effective user role
                effectiveUser.customer_id // Pass the correct customer ID
            );
        }
        handleCloseModifierModal();
    }, [selectedProduct, selectedModifiers, getEffectiveUserDetails]); // Add dependencies


    // --- Update addToCart signature ---
    const addToCart = (
        product: ProductWithImageRef,
        modifiers: Modifier[],
        imageRef: React.RefObject<HTMLImageElement> | undefined,
        price: number, // Receive the final calculated price
        userId: number | undefined, // Receive effective user ID
        userRole: string | undefined, // Receive effective user role
        customerId: number | undefined // Receive effective customer ID
    ) => {

        // Generate a unique ID for the cart item (e.g., product ID + modifier IDs + timestamp)
        const modifierIds = modifiers.map(m => m.id).sort().join('-');
        const cartItemId = `${product.id}-${modifierIds || 'base'}-${Date.now()}`;

        // Ensure customerId is defined before dispatching
        if (customerId === undefined) {
            console.error("Cannot add to cart: Customer ID is undefined.");
            toast.error("An error occurred. Customer information missing.");
            return;
        }

        dispatch({
            type: 'ADD_TO_CART',
            payload: {
                cartItemId: cartItemId, // Use the generated unique ID
                id: product.id, // product id
                product_id: product.id, // Add product_id if needed by backend/cart display
                product_category: product.product_cat,
                product_code: product.product_code,
                product_name: product.product_name,
                // Store SRP for reference, price is the calculated MTPrice/final price
                product_value: product.product_value,
                price: price, // Use the final price passed in
                quantity: 1, // Default quantity
                modifiers: modifiers,
                payment_for: 'product',
                // --- Add customer ID ---
                customer_id: customerId, // Associate cart item with the effective customer
            } as CartItem, // Assert type here if payload structure matches CartItem
        });

        if (imageRef?.current) { // Check if imageRef.current exists
            setAnimationQueue(prevQueue => [...prevQueue, { product, imageRef }]);
        }

        toast.info(`${product.product_name} added to cart!`, {
            position: "bottom-center",
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
    };

    // --- Remove the placeholder function ---
    // const getEffectiveUserDetails = () => { ... };


    return {
        handleAddToCart,
        showModifierModal,
        handleCloseModifierModal,
        modifiers,
        selectedProduct,
        handleModifierSelection,
        handleConfirmModifiers, // Expose the updated handler
        selectedModifiers,
        animationQueue,
    };
};

export interface Customer {
    id: number;
    name: string;
    email: string;
    phone: string;
    city: string;
    // other fields
    user_id: number;
    role: string; // Ensure this exists and is used
    // roles?: string[]; // Alternatively, if a user can have multiple roles
    country: string;
    state: string;

    first_name: string;
    last_name: string;
}

export interface OrderSummary {
    id: number;
    vendor_id: number; // owner shop id
    customer_id: number | null;
    order_type: string;
    table_id: number | null;
    items: OrderItem[]; // u8 Tranx id related to this order

    status: string;

    amount_to_pay: string;
    user_id: number;
    // table: TableDetails | null;

    total_taxes: string | null;
    receivable: string | null;
    rounding_value: string | null;
    rounded_receivable: string | null;
    received_amount: string | null;
    change: string | null;
    service_charges: string | null;
    total_amount: string | null;

    totalAmount: number;
    totalWithTax: number;
    totalTax: number;
    roundingDifference: number;

    created_at: string;
    updated_at: string;

    //abb 22-1-25
    balance: number;

    // process_log: string; // New field for process log

    order_uuid: string;
}

//abb 22-1-25
export interface OrderItem {

    // id: number;
    order_id: number;
    customer_id: number;

    product_id: number;
    product_code: string;

    product_name: string;
    product_category: number;
    quantity: number;
    product_value: number;

    price: number;
    total_price: number;
    // discount: string;
    // tax: string;
    // created_at: string;
    // updated_at: string;
    // deleted_at: string | null;

    // id: number;
    // // vendor_id: number;
    // order_id: number;
    // product_id: number;
    // product_name: string;
    // product_category: number;
    // price: string;
    // quantity: number;
    // total_price: string;
    // modifires: string;
    // // add_ons: string;
    // // toppings: string;
    // status: string;
    // items: string;
    // created_at: string;
    // updated_at: string;
    // // product: ProductDetails;
    // // specifications: Specification[];

    //abb 21-1-25
    payment_desc: string;
    payment_for: string;
    balance: number;
    unit_price: number;
    qty: number;
    tranx_details: string;
    category_id: number;

    // //abb 250214
    // print_to_location: string;

    modifiers: Modifier[]; // Add modifiers to OrderItem
    checkout: boolean;


}


export interface ApiCity {
    id: number; // Or string, depending on your API
    name: string;
    // Add other relevant city properties if needed
}

export interface ApiState {
    id: number; // Or string
    name: string;
    cities: ApiCity[]; // Eager loaded cities
    // Add other relevant state properties if needed
}

export interface ApiCountry {
    id: number; // Or string
    name: string;
    code: string; // e.g., 'MY', 'SG' - useful for matching
    phonecode: string; // e.g., '+60' - can reuse this!
    states: ApiState[]; // Eager loaded states
    // Add other relevant country properties if needed
}

// Interface for the overall API response if it's wrapped
export interface LocationsApiResponse {
    data: ApiCountry[];
    // Add other potential response fields like pagination, status, etc.
}